import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check } from "@/components/Check";
import {
  Users, Plus, Trash2, Lock, UserPlus, Activity, Briefcase, ListChecks,
} from "lucide-react";

const STAGES = ["Lead", "Qualified", "Under Contract", "Closed", "Lost"];
const PRIORITIES = ["High", "Medium", "Low"];

export default function Team() {
  const { user } = useAuth();
  const [teams, setTeams] = useState(null);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");

  const loadTeams = async () => {
    const { data } = await api.get("/teams/mine");
    setTeams(data);
  };
  useEffect(() => { loadTeams(); }, []);

  if (teams === null) {
    return <div className="mono text-muted-foreground uppercase tracking-widest text-xs">Loading…</div>;
  }

  if (teams.length === 0) {
    const isTeamPlan = user?.plan === "team";
    return (
      <div className="space-y-6" data-testid="team-page">
        <header>
          <div className="kpi-label">Team</div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
            Team workspace<span className="text-hud">_</span>
          </h1>
        </header>
        {isTeamPlan ? (
          <div className="hud-frame p-8 max-w-lg space-y-4" data-testid="team-create">
            <div className="kpi-label flex items-center gap-2"><Users size={11} /> CREATE YOUR WORKSPACE</div>
            <p className="text-sm text-muted-foreground">
              Shared pipelines, assigned tasks, roles and a live activity feed for your whole crew.
            </p>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Workspace name — e.g. Amaya Group"
              className="hud-input w-full"
              data-testid="team-name-input"
            />
            {err && <div className="mono text-[11px] uppercase tracking-widest text-[hsl(var(--oxblood))]">{err}</div>}
            <button
              onClick={async () => {
                setErr("");
                try { await api.post("/teams", { name }); loadTeams(); }
                catch (e) { setErr(e.response?.data?.detail || "Could not create team"); }
              }}
              className="hud-btn hud-btn-primary"
              data-testid="team-create-btn"
            >
              CREATE WORKSPACE
            </button>
          </div>
        ) : (
          <div className="hud-frame p-10 text-center space-y-4" data-testid="team-locked">
            <Lock size={20} className="mx-auto text-oud" />
            <div className="display text-xl uppercase">Team workspaces need the Team plan</div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Shared pipelines, task assignment, roles, team analytics and an activity feed — $20/mo.
              (If a teammate invited you, ask them to add your account email.)
            </p>
            <Link to="/billing" className="hud-btn hud-btn-primary inline-flex" data-testid="team-upgrade">
              UPGRADE TO TEAM — $20/MO
            </Link>
          </div>
        )}
      </div>
    );
  }

  return <Workspace team={teams[0]} me={user} />;
}

function Workspace({ team, me }) {
  const [detail, setDetail] = useState(null);
  const [overview, setOverview] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [deals, setDeals] = useState([]);
  const [activity, setActivity] = useState([]);
  const tid = team.id;
  const canManage = ["owner", "admin"].includes(team.my_role);

  const loadAll = async () => {
    const [d, o, t, dl, a] = await Promise.all([
      api.get(`/teams/${tid}`).then((r) => r.data),
      api.get(`/teams/${tid}/overview`).then((r) => r.data),
      api.get(`/teams/${tid}/tasks`).then((r) => r.data),
      api.get(`/teams/${tid}/deals`).then((r) => r.data),
      api.get(`/teams/${tid}/activity`).then((r) => r.data),
    ]);
    setDetail(d); setOverview(o); setTasks(t); setDeals(dl); setActivity(a);
  };
  useEffect(() => { loadAll(); }, [tid]); // eslint-disable-line

  if (!detail || !overview) {
    return <div className="mono text-muted-foreground uppercase tracking-widest text-xs">Loading workspace…</div>;
  }

  const members = detail.members;
  const memberName = (uid) => {
    const m = members.find((x) => x.user_id === uid);
    return m ? (m.name || m.email.split("@")[0]) : "—";
  };

  return (
    <div className="space-y-8" data-testid="team-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kpi-label flex items-center gap-2"><Users size={11} /> TEAM WORKSPACE · ROLE: {team.my_role.toUpperCase()}</div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight" data-testid="team-name">
            {detail.team.name}
          </h1>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[
            { l: "MEMBERS", v: overview.members_count },
            { l: "TASKS DONE", v: `${overview.tasks_done}/${overview.tasks_total}` },
            { l: "OPEN DEALS", v: overview.deals_open },
            { l: "PIPELINE", v: fmtMoney(overview.deals_value) },
          ].map((s) => (
            <div key={s.l} className="border border-border px-4 py-3">
              <div className="kpi-label">{s.l}</div>
              <div className="display text-xl mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <TasksCard tid={tid} tasks={tasks} setTasks={setTasks} members={members} memberName={memberName} refresh={loadAll} />
          <DealsCard tid={tid} deals={deals} setDeals={setDeals} refresh={loadAll} />
        </div>
        <div className="space-y-4">
          <MembersCard tid={tid} detail={detail} canManage={canManage} isOwner={team.my_role === "owner"} me={me} refresh={loadAll} />
          <div className="hud-frame p-6" data-testid="team-activity">
            <div className="kpi-label flex items-center gap-2"><Activity size={11} /> ACTIVITY FEED</div>
            <div className="mt-3 space-y-2 max-h-80 overflow-y-auto">
              {activity.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
              {activity.map((a) => (
                <div key={a.id} className="text-sm">
                  <span className="text-oud">{a.actor?.split("@")[0]}</span>{" "}
                  <span className="text-muted-foreground">{a.action}</span>
                  {a.detail && <span> · {a.detail}</span>}
                  <div className="mono text-[9px] text-muted-foreground/60 uppercase">{(a.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="hud-frame p-6" data-testid="team-analytics">
            <div className="kpi-label">TEAM ANALYTICS · TASKS BY MEMBER</div>
            <div className="mt-3 space-y-2">
              {overview.per_member.map((m) => (
                <div key={m.user_id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">{m.name} <span className="mono text-[9px] text-muted-foreground uppercase">({m.role})</span></span>
                  <span className="mono text-[11px] text-oud tabular-nums">{m.tasks_done}/{m.tasks_total}</span>
                </div>
              ))}
              {overview.tasks_unassigned > 0 && (
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {overview.tasks_unassigned} unassigned task{overview.tasks_unassigned === 1 ? "" : "s"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TasksCard({ tid, tasks, setTasks, members, memberName, refresh }) {
  const [title, setTitle] = useState("");
  const add = async () => {
    if (!title.trim()) return;
    const { data } = await api.post(`/teams/${tid}/tasks`, { title });
    setTasks((p) => [data, ...p]);
    setTitle("");
  };
  const update = async (t, patch) => {
    setTasks((p) => p.map((x) => (x.id === t.id ? { ...x, ...patch } : x)));
    await api.put(`/teams/${tid}/tasks/${t.id}`, patch);
    refresh();
  };
  const del = async (t) => {
    setTasks((p) => p.filter((x) => x.id !== t.id));
    await api.delete(`/teams/${tid}/tasks/${t.id}`);
  };
  return (
    <div className="hud-frame p-6" data-testid="team-tasks">
      <div className="kpi-label flex items-center gap-2"><ListChecks size={11} /> SHARED TASKS</div>
      <div className="flex gap-2 mt-4">
        <input value={title} onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="New team task…" className="hud-input flex-1" data-testid="team-task-input" />
        <button onClick={add} className="hud-btn" data-testid="team-task-add"><Plus size={12} /></button>
      </div>
      <div className="mt-4 space-y-2">
        {tasks.map((t) => (
          <div key={t.id} className="flex items-center gap-2 group" data-testid={`team-task-${t.id}`}>
            <Check size="sm" checked={!!t.done} onChange={(v) => update(t, { done: v })} testid={`team-task-check-${t.id}`} />
            <span className={`text-sm flex-1 min-w-0 truncate ${t.done ? "line-through opacity-50" : ""}`}>{t.title}</span>
            <select
              value={t.priority || ""}
              onChange={(e) => update(t, { priority: e.target.value || null })}
              className="hud-input !py-0.5 !text-[10px] mono uppercase w-20"
              data-testid={`team-task-priority-${t.id}`}
            >
              <option value="">PRIO</option>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
            </select>
            <select
              value={t.assigned_to || ""}
              onChange={(e) => update(t, { assigned_to: e.target.value || null })}
              className="hud-input !py-0.5 !text-[10px] mono uppercase w-32"
              data-testid={`team-task-assign-${t.id}`}
            >
              <option value="">UNASSIGNED</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>{(m.name || m.email.split("@")[0]).toUpperCase()}</option>
              ))}
            </select>
            <button onClick={() => del(t)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-[hsl(var(--signal))]" data-testid={`team-task-del-${t.id}`}>
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        {tasks.length === 0 && <div className="text-sm text-muted-foreground">No shared tasks yet.</div>}
      </div>
    </div>
  );
}

function DealsCard({ tid, deals, setDeals, refresh }) {
  const add = async () => {
    const { data } = await api.post(`/teams/${tid}/deals`, {});
    setDeals((p) => [data, ...p]);
  };
  const update = async (d, patch) => {
    setDeals((p) => p.map((x) => (x.id === d.id ? { ...x, ...patch } : x)));
    await api.put(`/teams/${tid}/deals/${d.id}`, patch);
    refresh();
  };
  const del = async (d) => {
    setDeals((p) => p.filter((x) => x.id !== d.id));
    await api.delete(`/teams/${tid}/deals/${d.id}`);
  };
  return (
    <div className="hud-frame p-6" data-testid="team-deals">
      <div className="flex items-center justify-between">
        <div className="kpi-label flex items-center gap-2"><Briefcase size={11} /> SHARED PIPELINE</div>
        <button onClick={add} className="hud-btn" data-testid="team-deal-add"><Plus size={12} /> DEAL</button>
      </div>
      <div className="mt-4 space-y-2">
        {deals.map((d) => (
          <div key={d.id} className="grid grid-cols-12 gap-2 items-center group" data-testid={`team-deal-${d.id}`}>
            <input defaultValue={d.client} onBlur={(e) => e.target.value !== d.client && update(d, { client: e.target.value })}
              placeholder="Client" className="hud-input !py-1 !text-sm col-span-3" data-testid={`team-deal-client-${d.id}`} />
            <input defaultValue={d.property} onBlur={(e) => e.target.value !== d.property && update(d, { property: e.target.value })}
              placeholder="Property / subject" className="hud-input !py-1 !text-sm col-span-4" />
            <select value={d.stage || "Lead"} onChange={(e) => update(d, { stage: e.target.value })}
              className="hud-input !py-1 !text-[10px] mono uppercase col-span-2" data-testid={`team-deal-stage-${d.id}`}>
              {STAGES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
            <input defaultValue={d.value ?? ""} onBlur={(e) => update(d, { value: parseFloat(e.target.value) || 0 })}
              placeholder="$" inputMode="decimal" className="hud-input !py-1 !text-sm col-span-2 text-right" />
            <button onClick={() => del(d)} className="col-span-1 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-[hsl(var(--signal))]">
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        {deals.length === 0 && <div className="text-sm text-muted-foreground">No shared deals yet.</div>}
      </div>
    </div>
  );
}

function MembersCard({ tid, detail, canManage, isOwner, me, refresh }) {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState(null);
  const invite = async () => {
    if (!email.trim()) return;
    setMsg(null);
    try {
      const { data } = await api.post(`/teams/${tid}/invite`, { email });
      setMsg({ ok: true, text: data.joined ? "Member added" : "Invite pending — they'll join when they sign up" });
      setEmail("");
      refresh();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Could not invite" });
    }
  };
  return (
    <div className="hud-frame p-6" data-testid="team-members">
      <div className="kpi-label flex items-center gap-2"><UserPlus size={11} /> MEMBERS &amp; ROLES</div>
      {canManage && (
        <div className="flex gap-2 mt-4">
          <input value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && invite()}
            placeholder="teammate@email.com" className="hud-input flex-1" data-testid="team-invite-input" />
          <button onClick={invite} className="hud-btn" data-testid="team-invite-btn">INVITE</button>
        </div>
      )}
      {msg && (
        <div className={`mono text-[10px] uppercase tracking-widest mt-2 ${msg.ok ? "text-oud" : "text-[hsl(var(--oxblood))]"}`} data-testid="team-invite-msg">
          {msg.text}
        </div>
      )}
      <div className="mt-4 space-y-2">
        {detail.members.map((m) => (
          <div key={m.user_id} className="flex items-center gap-2 text-sm" data-testid={`team-member-${m.user_id}`}>
            <span className="flex-1 truncate">{m.name || m.email}</span>
            {isOwner && m.role !== "owner" ? (
              <select value={m.role} onChange={async (e) => { await api.put(`/teams/${tid}/members/${m.user_id}/role`, { role: e.target.value }); refresh(); }}
                className="hud-input !py-0.5 !text-[10px] mono uppercase w-24" data-testid={`team-role-${m.user_id}`}>
                <option value="admin">ADMIN</option>
                <option value="member">MEMBER</option>
              </select>
            ) : (
              <span className="mono text-[9px] uppercase tracking-widest text-oud border border-oud/40 px-1.5 py-0.5">{m.role}</span>
            )}
            {canManage && m.role !== "owner" && m.user_id !== me?.id && (
              <button onClick={async () => { await api.delete(`/teams/${tid}/members/${m.user_id}`); refresh(); }}
                className="text-muted-foreground hover:text-[hsl(var(--signal))]" data-testid={`team-remove-${m.user_id}`}>
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
        {detail.pending_invites.map((i) => (
          <div key={i.id} className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="flex-1 truncate">{i.email}</span>
            <span className="mono text-[9px] uppercase tracking-widest border border-dashed border-border px-1.5 py-0.5">PENDING</span>
          </div>
        ))}
      </div>
    </div>
  );
}
